// filepath: apps/web/src/features/lists/tree/IndentGuides.tsx
//
// FR-UI-VIEW-2 AC-1 + design D1 hand-off — vertical guide lines that
// indicate nesting depth for each TreeNode.
//
// Each guide is a 1px-wide vertical line stretched to the row height
// of the parent. Lines are spaced at `var(--space-6)` (the
// design-system indent step) so adjacent levels align with the row
// content. Color is `--border-default` — no raw hex, no named CSS
// colors (FR-UI-NFR-4 AC-5c).
//
// The component renders exactly `depth` lines. When `depth === 0`
// it returns `null` (root nodes have no guides — saves a DOM
// node + matches the visual identity of the kanban empty-column
// edge case).
//
// ISS-303 invariant: every interactive element carries a
// `data-testid`. IndentGuides renders decorative elements only
// (no event handlers) — each line gets a stable
// `data-testid="tree-indent-guide-{depth}"` so the runtime test
// harness can count guides against a depth value.

import React from "react";

export interface IndentGuidesProps {
  /**
   * Number of guide lines to render. 0 (root node) renders
   * nothing; 1+ renders that many stack-aligned vertical lines.
   */
  depth: number;
  /**
   * Test hook forwarded onto the wrapping container. Defaults to
   * `tree-indent-guides` when omitted.
   */
  "data-testid"?: string;
}

export function IndentGuides({
  depth,
  "data-testid": testId = "tree-indent-guides",
}: IndentGuidesProps): React.ReactElement | null {
  if (depth <= 0) return null;
  // Pre-build the line array once per depth value. The cost is
  // O(depth), which is bounded by MaxTreeDepthCap = 64, so the
  // allocation is constant in practice.
  const lines = Array.from({ length: depth }, (_, idx) => idx);
  return (
    <div
      data-testid={testId}
      data-tree-indent-depth={depth}
      aria-hidden="true"
      style={{
        display: "flex",
        // Indent step — the design-system `--space-6` (24px). The
        // line width is 1px border-left, so the container is sized
        // to (n * 24px) of usable space + (n * 1px) of guide width.
        // The flex container stamps its children at exact 24px
        // intervals, no hand-rolled minWidth / maxWidth per row.
        gap: "calc(var(--space-6) - 1px)",
        alignItems: "stretch",
        paddingRight: "var(--space-2)",
        flexShrink: 0,
      }}
    >
      {lines.map((idx) => (
        <span
          key={idx}
          aria-hidden="true"
          data-testid={`tree-indent-guide-${idx}`}
          style={{
            display: "block",
            width: "1px",
            // Full-height line so the guide visually spans the
            // whole node row. `height: 100%` here is layout-side
            // (size of the row), not a stylistic pixel value —
            // design-system-token-only.
            height: "100%",
            background: "var(--border-default)",
            flexShrink: 0,
          }}
        />
      ))}
    </div>
  );
}
